import React, { useState } from 'react';
import { Boxes, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { adminAPI } from '../services/api';

const inputCls = 'mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-900 bg-white';
const MODES = [['set', 'Définir à'], ['add', 'Ajouter'], ['remove', 'Retirer']];
const PRESETS = { set: [0, 5, 10, 20, 50, 100], add: [1, 5, 10, 20, 50], remove: [1, 5, 10, 20, 50] };

const nextStock = (old, mode, value) => (mode === 'set' ? value : Math.max(0, old + (mode === 'add' ? value : -value)));

export const BulkStockDialog = ({ open, onClose, selectedIds, sampleProducts = [], onDone }) => {
  const [mode, setMode] = useState('set');
  const [value, setValue] = useState(10);
  const [busy, setBusy] = useState(false);
  const qty = Math.max(0, parseInt(value, 10) || 0);
  const canApply = Number.isInteger(parseInt(value, 10)) && (mode === 'set' ? qty >= 0 : qty > 0);

  const apply = async () => {
    setBusy(true);
    try {
      const r = await adminAPI.bulkStock(selectedIds, mode === 'set' ? 'set' : 'add', mode === 'remove' ? -qty : qty);
      toast.success(`${r.message}${r.skipped?.length ? ` · ${r.skipped.length} inchangé(s)` : ''}`);
      onDone?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Opération impossible');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="bulk-stock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Boxes size={18} className="text-orange-500" /> Stock groupé — {selectedIds.length} produit(s)</DialogTitle>
          <DialogDescription>Le stock entrepôt des produits cochés est mis à jour en une fois. Chaque changement est tracé dans l'historique et annulable depuis la liste des opérations.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="inline-flex rounded-md border border-gray-200 p-0.5 bg-gray-50 text-sm" role="tablist">
            {MODES.map(([m, l]) => (
              <button key={m} type="button" role="tab" aria-selected={mode === m} data-testid={`bulk-stock-mode-${m}`} onClick={() => setMode(m)} className={`px-3 py-1.5 rounded ${mode === m ? 'bg-white shadow font-medium text-gray-900' : 'text-gray-500'}`}>{l}</button>
            ))}
          </div>
          <label className="block text-sm text-gray-700">{mode === 'set' ? 'Nouveau stock (unités)' : 'Quantité (unités)'}
            <input type="number" step="1" min="0" inputMode="numeric" value={value} onChange={(e) => setValue(e.target.value)} data-testid="bulk-stock-value" className={inputCls} />
          </label>
          <div className="flex flex-wrap gap-2">
            {PRESETS[mode].map((v) => (
              <button key={v} type="button" onClick={() => setValue(v)} data-testid={`bulk-stock-preset-${v}`} className={`text-xs px-2.5 py-1 rounded-full border ${qty === v && String(value) !== '' ? 'bg-orange-500 border-orange-500 text-white' : 'border-gray-300 text-gray-700 hover:bg-gray-50'}`}>{mode === 'set' ? `= ${v}` : `${mode === 'add' ? '+' : '−'}${v}`}</button>
            ))}
          </div>
          {mode === 'remove' && <p className="text-xs text-gray-500">Le stock ne descend jamais sous 0.</p>}
          {sampleProducts.length > 0 && (
            <div className="rounded-md bg-gray-50 border border-gray-200 p-3 text-xs" data-testid="bulk-stock-preview">
              <p className="font-medium text-gray-700 mb-1.5">Aperçu</p>
              <ul className="space-y-1">
                {sampleProducts.slice(0, 4).map((p) => {
                  const old = p.stock ?? 0;
                  const nv = nextStock(old, mode, qty);
                  return (
                    <li key={p.id} className="flex justify-between gap-3">
                      <span className="truncate text-gray-600">{p.name}</span>
                      <span className="whitespace-nowrap"><span className="text-gray-400 line-through mr-1">{old}</span><span className={`font-semibold ${nv === 0 ? 'text-red-600' : 'text-gray-900'}`}>{nv}</span></span>
                    </li>
                  );
                })}
                {sampleProducts.length > 4 && <li className="text-gray-400">… et {sampleProducts.length - 4} autre(s)</li>}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} data-testid="bulk-stock-cancel">Annuler</Button>
          <Button type="button" onClick={apply} disabled={busy || !canApply} data-testid="bulk-stock-apply" className="bg-orange-500 hover:bg-orange-600 text-white">
            {busy ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Boxes size={14} className="mr-1" />} Appliquer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
